// Supabase adapter is fully disabled. We keep a compatible shape that never performs
// any network calls, returning safe empty results so features importing this module
// won’t crash. This satisfies “no supabase” while avoiding large refactors.

type Result<T> = { data: T; error: any };

function makeResult<T>(data: T): Result<T> {
  return { data, error: null } as any;
}

// Thenable builder that allows chaining (order, eq, select, single, etc.) and resolves
// to a final static result when awaited.
function makeBuilder<T>(finalData: T) {
  const result = makeResult(finalData);
  const builder: any = {
    order: () => builder,
    eq: () => builder,
    limit: () => builder,
    select: () => builder,
    insert: () => builder,
    update: () => builder,
    delete: () => builder,
    single: () => builder,
    then: (res: any, rej: any) => Promise.resolve(result).then(res, rej),
    catch: (rej: any) => Promise.resolve(result).catch(rej),
    finally: (cb: any) => Promise.resolve(result).finally(cb),
  };
  return builder;
}

export const supabase = {
  functions: {
    invoke: async (_name: string, _opts?: any) => makeResult(null),
  },

  from: (_tableName: string) => ({
    select: (_cols = '*') => makeBuilder<any[]>([]),
    insert: (_payload: any) => makeBuilder<any>(null),
    update: (_payload: any) => makeBuilder<any>(null),
    delete: (_query: any) => makeBuilder<any>(null),
  }),

  rpc: async (_fnName: string, _params?: any) => makeResult(null),

  auth: {
    // Never call the network; read minimal user if present from sessionStorage
    getUser: async () => {
      try {
        const raw = typeof window !== 'undefined' ? window.sessionStorage.getItem('userData') : null;
        const parsed = raw ? JSON.parse(raw) : null;
        const user = parsed
          ? {
              ...parsed,
              id: parsed.id ?? parsed.authUserId ?? parsed.UniqueId ?? parsed.userId ?? null,
              email: parsed.email ?? parsed.username ?? parsed.userEmail ?? null,
              name: parsed.nameOfUser ?? parsed.NameOfUser ?? parsed.name ?? null,
            }
          : null;
        return makeResult({ user });
      } catch {
        return makeResult({ user: null });
      }
    },
    signInWithOtp: async (_opts?: any) => makeResult(null),
    signOut: async () => makeResult(null),
    portalLogin: async (_payload: { username: string; password: string }) => makeResult(null),
  },
} as any;

// Minimal storage shim (no network)
(supabase as any).storage = {
  from: (_bucketName: string) => ({
    createSignedUrl: async (_path: string, _expiresInSeconds: number) => makeResult({ signedUrl: null }),
    getPublicUrl: (_path: string, _opts?: any) => ({ data: { publicUrl: '' } }),
  }),
};
